import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import InfiniteScroll from 'react-infinite-scroller';
import { useTracker } from '@tribeplatform/react-sdk';
import { Card } from '@tribeplatform/react-ui-kit/Card';
import { FeaturesList } from '../common/constants/features';
import { PostCardListLoading, PostRowListLoading, } from '../LoadingStates/PostCardLoading';
import { Post } from '../Post';
import { ReplyWithParent } from '../Reply/ReplyWithParent';
const MARK_POST_AS_VIEWED_AFTER_MS = 1000;
const observerOptions = {
    root: null,
    rootMargin: '0px 0px -30% 0px',
    threshold: 1.0,
};
export const GenericPostList = ({ posts, context, pinnedPosts = [], activeTagId = '', activeTagSlug = '', emptyState, fetchNextPage, hasNextPage, isLoading, isFetched, isFetchingNextPage, view = 'card', }) => {
    const tracker = useTracker();
    const [activeView, setActiveView] = useState(view);
    const scrollObserverRef = useRef();
    const viewedPosts = useRef({});
    const timeOutHandles = useRef({});
    const [decisionViewPostOnVisible] = useDecision(FeaturesList.ViewPostOnVisible);
    const markPostAsViewed = useCallback((postId) => {
        viewedPosts.current[postId] = true;
        timeOutHandles.current[postId] = undefined;
        if (decisionViewPostOnVisible.enabled) {
            tracker.setTarget({ postId });
            tracker.trackPageView(postId);
        }
    }, [decisionViewPostOnVisible.enabled, tracker]);
    const startTimer = useCallback((postId) => {
        timeOutHandles.current[postId] = setTimeout(() => markPostAsViewed(postId), MARK_POST_AS_VIEWED_AFTER_MS);
    }, [markPostAsViewed]);
    const stopTimer = useCallback((postId) => {
        clearTimeout(timeOutHandles.current[postId]);
        timeOutHandles.current[postId] = undefined;
    }, []);
    useEffect(() => {
        setActiveView(view);
    }, [view]);
    useEffect(() => {
        scrollObserverRef.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                var _a, _b, _c, _d, _e;
                const postId = (_b = (_a = entry.target) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
                if (entry.isIntersecting) {
                    if (!((_c = viewedPosts.current) === null || _c === void 0 ? void 0 : _c[postId]) &&
                        !((_d = timeOutHandles.current) === null || _d === void 0 ? void 0 : _d[postId])) {
                        startTimer(postId);
                    }
                }
                else if ((_e = timeOutHandles.current) === null || _e === void 0 ? void 0 : _e[postId]) {
                    stopTimer(postId);
                }
            });
        }, observerOptions);
        return () => {
            if (scrollObserverRef.current) {
                scrollObserverRef.current.disconnect();
            }
        };
    }, [startTimer, stopTimer]);
    const content = (_jsxs(_Fragment, { children: [pinnedPosts.map(post => {
                return (_jsx(Post, { post: post, context: context, activeTagId: activeTagId, activeTagSlug: activeTagSlug, view: activeView, scrollObserver: scrollObserverRef.current }, post.id));
            }), posts.map(post => {
                if (post === null || post === void 0 ? void 0 : post.repliedToId) {
                    return (_jsx(ReplyWithParent, { reply: post, context: context }, post.id));
                }
                return (_jsx(Post, { post: post, context: context, activeTagId: activeTagId, activeTagSlug: activeTagSlug, view: activeView, scrollObserver: scrollObserverRef.current }, post.id));
            })] }, void 0));
    if (isFetched &&
        !isLoading &&
        !(posts === null || posts === void 0 ? void 0 : posts.length) &&
        !(pinnedPosts === null || pinnedPosts === void 0 ? void 0 : pinnedPosts.length) &&
        emptyState) {
        return _jsx(_Fragment, { children: emptyState }, void 0);
    }
    return (_jsx("div", { className: "post-list", children: _jsx(InfiniteScroll, { pageStart: 0, loadMore: fetchNextPage, hasMore: hasNextPage && !isLoading && !isFetchingNextPage, threshold: 800, children: activeView === 'row' ? (_jsxs(Card, { className: "flex flex-col divide-y divide-line-subdued", children: [content, !isFetched || isLoading || isFetchingNextPage ? (_jsx(PostRowListLoading, {}, void 0)) : null] }, void 0)) : (_jsxs("div", { className: "flex flex-col space-y-5", children: [content, !isFetched || isLoading || isFetchingNextPage ? (_jsx(PostCardListLoading, {}, void 0)) : null] }, void 0)) }, void 0) }, void 0));
};
//# sourceMappingURL=GenericPostList.js.map